import { computed, inject, Injectable, resource, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AuthenticationService } from '../authentication/authentication.service';
import { LegalEntityModelMapper } from './legal-entity-model.mapper';
import { CountryConfiguration, User } from './user';
import { UserClient } from './user.client';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly authService = inject(AuthenticationService);
  private readonly userClient = inject(UserClient);
  private readonly legalEntityModelMapper = inject(LegalEntityModelMapper);

  /**
   * Subscribe the onUserChange to detect a user change.
   */
  private readonly userResource = resource({
    params: () => this.authService.authHeadersWithoutAdminDpNr() || this.authService.error(),
    loader: () => {
      const error = this.authService.error();
      if (error) throw error;
      return this.userClient.getUserInfo();
    },
  });

  public readonly userError = computed(() => this.userResource.error());

  public readonly doneLoading = computed(() => ['error', 'resolved'].includes(this.userResource.status()));

  private readonly userComputed$ = toObservable(this.doneLoading).pipe(filter((computed) => computed));

  public readonly userSignal = computed(() => (this.userResource.hasValue() ? this.userResource.value() : undefined));

  private readonly user$ = toObservable(this.userSignal).pipe(filter((value) => !!value));

  private readonly updatedCountryConfiguration = signal<CountryConfiguration | undefined>(undefined);

  /**
   * Gets the latest user information.
   * User information might get updated, when access token change.
   * To subscribe to updates use the userSignal.
   */
  public async user(): Promise<User> {
    return await firstValueFrom(this.user$);
  }

  /**
   * Check if the user is allowed to use a functionality by their roles.
   *
   * @param rolesToCheck which are allowed to use the functionality
   */
  public async isAllowed(...rolesToCheck: readonly string[]) {
    await firstValueFrom(this.userComputed$);
    return this.userIsAllowed(rolesToCheck);
  }

  /**
   * Check if the user’s business partner ID is whitelisted.
   */
  public async isWhitelisted() {
    await firstValueFrom(this.userComputed$);
    return this.userOutletIsWhitelisted();
  }

  /**
   * Check if the user is allowed to use a functionality by their roles.
   *
   * @param rolesToCheck which are allowed to use the functionality
   */
  public userIsAllowed(rolesToCheck: readonly string[]) {
    const user = this.userSignal();
    return !!user && rolesToCheck.some((role) => user.roles.includes(role));
  }

  /**
   * Check if the user’s business partner ID is whitelisted.
   *
   * This can be ignored when admin mode is turned on.
   */
  public userOutletIsWhitelisted() {
    const user = this.userSignal();
    return !!user && user.businessPartnerIdIsWhitelisted;
  }

  /**
   * The legal entities available to select in the admin mode.
   */
  readonly legalEntitiesForAdminMode = computed(() =>
    this.legalEntityModelMapper.mapToLegalEntityMap(this.userSignal()?.availableAdminLegalEntities ?? []),
  );

  readonly countryConfiguration = computed(() => {
    const user = this.userSignal();
    if (!user) {
      return undefined;
    }
    const updated = this.updatedCountryConfiguration();
    return updated ? updated : user.countryConfiguration;
  });

  public updateCountryConfiguration(updatedCountryConfiguration: CountryConfiguration) {
    this.updatedCountryConfiguration.set(updatedCountryConfiguration);
  }
}
